import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FC, useState, useEffect } from "react";
import {
  Button,
  Flyout,
  FlyoutHeader,
  FlyoutTitle,
} from "@monolith-forensics/monolith-ui";

import {
  ChevronLeftIcon,
  ChevronRightIcon,
  EditIcon,
  TrashIcon,
} from "lucide-react";

import {
  DetailItem,
  DetailLabel,
  DetailValue,
  FlyoutContainer,
  HeaderContent,
} from "@/components/Flyouts";

import { getClients } from "@/api";
import DeleteClientModal from "../Modals/DeleteClientModal";
import EditClientModal from "../Modals/EditClientModal";
import CustomFieldsApi from "@/api/custom-fields";
import { CUSTOM_FIELD_CATEGORIES } from "@/pages/Settings/CustomFieldSettings/utils/customFieldConstants";
import { addCustomFieldsToColumnDefs } from "@/components/Evidence/CustomFieldCell";
import { monolithMoment } from "@/utils/date-format";

import { Client, CustomAttribute } from "@/types";
import ClientsColumnDefs, {
  ClientColumnDefinition,
} from "../ClientsColumnDefs";

interface ClientFlyoutProps {
  show: boolean;
  onClose?: () => void;
  defaultInfo: Client;
  onEdit: () => void;
  onDelete: () => void;
  onNext: () => void;
  onPrevious: () => void;
}

interface FlyoutDetailsProps {
  defaultData: Client;
}

const MainDetails: FC<FlyoutDetailsProps> = ({ defaultData }) => {
  const [columnState, setColumnState] =
    useState<ClientColumnDefinition[]>(ClientsColumnDefs);
  const dataFieldIgnoreList = ["notes"];

  // Fetch custom fields for clients (using PEOPLE category same as contacts)
  const { data: customFields } = useQuery({
    queryKey: ["clients", "customFields", "list"],
    queryFn: () =>
      CustomFieldsApi.getCustomFields({
        category: CUSTOM_FIELD_CATEGORIES.PEOPLE,
      }),
  });

  // Update column definitions with custom fields
  useEffect(() => {
    if (!customFields) return;
    setColumnState((cs) => {
      return addCustomFieldsToColumnDefs(customFields, cs);
    });
  }, [customFields]);

  // Transform data to include custom field values
  const data: Client & Record<string, any> = {
    ...defaultData,
    ...(defaultData.custom_attributes?.reduce(
      (
        acc: Record<string, string | number | string[]>,
        attr: CustomAttribute
      ) => {
        acc[`custom_field_${attr.field_id}`] = attr.value;
        return acc;
      },
      {} as Record<string, string | number | string[]>
    ) || {}),
  };

  return (
    <FlyoutContainer>
      <div className="event-details">
        {columnState
          .filter((col) => !dataFieldIgnoreList.includes(col.dataField))
          .filter((col) =>
            col.dataField.includes("custom_field_")
              ? !!data[col.dataField]
              : true
          )
          .sort((a, b) => {
            // Sort custom fields alphabetically by caption, keep regular fields in original order
            const aIsCustom = a.dataField.includes("custom_field_");
            const bIsCustom = b.dataField.includes("custom_field_");

            if (aIsCustom && bIsCustom) {
              return a.caption.localeCompare(b.caption);
            }

            // Keep original order for non-custom fields
            return 0;
          })
          .map((col) => {
            const key = col.dataField;
            const label = col.caption;
            let value: React.ReactNode;

            if (col?.render) {
              value = col.render(data);
            } else if (col.dataType === "date") {
              value = monolithMoment({
                timestamp: data[key],
                includeTime: true,
              });
            } else {
              value = data[key];
            }

            return (
              <DetailItem key={key}>
                <DetailLabel>{label}</DetailLabel>
                <DetailValue>{value}</DetailValue>
              </DetailItem>
            );
          })}
        <DetailItem>
          <DetailLabel>Notes</DetailLabel>
        </DetailItem>
        <DetailValue>
          {defaultData.notes || "No notes for this client."}
        </DetailValue>
      </div>
    </FlyoutContainer>
  );
};

export const ClientFlyout: React.FC<ClientFlyoutProps> = ({
  show,
  onClose,
  defaultInfo,
  onEdit,
  onDelete,
  onNext,
  onPrevious,
}) => {
  const queryClient = useQueryClient();
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const { data: itemData, isLoading } = useQuery({
    queryKey: [
      "client",
      "item",
      defaultInfo.client_id,
      { include_custom_attributes: true },
    ],
    queryFn: () =>
      getClients({
        client_id: defaultInfo.client_id,
        include_custom_attributes: true,
      }),
    initialData: [defaultInfo],
    select: (res) => {
      return res[0];
    },
  });

  return (
    <Flyout open={show} onClose={() => onClose?.()}>
      {isLoading && <div>Loading...</div>}
      {!isLoading && (
        <>
          <FlyoutHeader>
            <FlyoutTitle>{itemData.name}</FlyoutTitle>
            <HeaderContent>
              <div>Client</div>
              <Button
                size="xxs"
                variant="text"
                leftSection={<EditIcon size={14} />}
                color="primary"
                onClick={() => setShowEditModal(true)}
              >
                Edit
              </Button>
              <Button
                size="xxs"
                variant="text"
                leftSection={<TrashIcon size={14} />}
                color="error"
                onClick={() => setShowDeleteModal(true)}
              >
                Delete
              </Button>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 10,
                  alignItems: "center",
                  marginLeft: "auto",
                }}
              >
                <Button
                  size="xxs"
                  variant="outlined"
                  onClick={() => onPrevious?.()}
                  style={{ padding: "2px 4px" }}
                >
                  <ChevronLeftIcon size={14} />
                </Button>
                <Button
                  size="xxs"
                  variant="outlined"
                  onClick={() => onNext?.()}
                  style={{ padding: "2px 4px" }}
                >
                  <ChevronRightIcon size={14} />
                </Button>
              </div>
            </HeaderContent>
          </FlyoutHeader>
          <MainDetails defaultData={itemData} />
          <EditClientModal
            onSubmit={() => {
              queryClient.invalidateQueries({
                queryKey: [
                  "client",
                  "item",
                  defaultInfo.client_id,
                  { include_custom_attributes: true },
                ],
              });
              queryClient.invalidateQueries({
                queryKey: ["clients", "list"],
              });
              onEdit();
            }}
            show={showEditModal}
            onClose={() => setShowEditModal(false)}
            onCancel={() => setShowEditModal(false)}
            defaultInfo={itemData}
          />
          <DeleteClientModal
            show={showDeleteModal}
            onCancel={() => setShowDeleteModal(false)}
            onClose={() => setShowDeleteModal(false)}
            onSubmit={() => {
              onDelete?.(); // Refetches queries in client section
              setShowDeleteModal(false);
            }}
            defaultInfo={itemData}
          />
        </>
      )}
    </Flyout>
  );
};
